//If there is desire to iterate over store, index or cursor (making them AsyncIterable) use this:
// import { openDB } from 'idb/with-async-ittr';
import { openDB, deleteDB } from 'idb';
import type {
  IDBPDatabase,
  IndexKey,
  IndexNames,
  StoreKey,
  StoreNames,
  StoreValue,
} from 'idb';
import { chain } from 'obliterator';
import type { Group } from 'three';

import type { PrepareAndMakeRequestOptions } from '@frontend/api';
import { AbortError, isAbortErrorLike } from '@frontend/controllers';
import type { CompositeProgressController } from '@frontend/controllers';
import {
  SimpleProgressController,
  SimpleProgressControllerOptions,
  FetchProgressController,
} from '@frontend/controllers';
import type { MultiMap } from '@frontend/datatypes';
import {
  fetchAndTrack,
  microWait,
  WaitUntilSetController,
} from '@frontend/utils';

import type { PMIJson } from './contracts/PMIJson';
import type { AssemblyJson } from './contracts/assembly';
import type { AssemblyInstanceJson } from './contracts/assemblyInstance';
import type { BodyJson } from './contracts/body';
import type { AnyPartJson } from './contracts/part';
import type { TessJson, STLTessJson } from './contracts/tess';
import Assembly from './loaders/assembly';
import AssemblyInstance from './loaders/assemblyInstance';
import { Body, type AnyBody } from './loaders/body';
import { Part, type AnyPart, type PartGeometryToGet } from './loaders/part';
import { PMIData } from './loaders/pmi/PMIData';
import { type CloneableSTEPGeometry } from './loaders/step/STEPGeometry';
import { type CloneableSTLGeometry } from './loaders/stl/STLGeometry';
import type { Renderer } from './renderer/renderer';

const partViewerDB = 'partViewer' as const;
const partViewerDBVersion = 18; //Increment every time we change fetched data or how we transform data we cache
//PMI data with version <= minPMIVersion will not be parsed
//Why? Prior to minPMIVersion, PMI data created by the PMI parser may have bugs
//* 1.1.8 fixes bug with tessellated leaders from solidworks/creo PP-9983.
//  * PMI parsed from < 1.1.8 versions will have incorrect tessellation that the frontend cannot correct.
//    So we don't display it.
const minPMIVersion = '1.1.8' as const;
const viewerDataStoreName = 'viewerData' as const;
const partTessellationStoreName = 'tess' as const;
const viewerDataPMIStoreName = 'pmi' as const;

const dbVersionChangeEventName = 'versionchange';
const dbCloseEventName = 'close';

type ViewerDataStore<
  S extends StoreNames<PartViewerDBSchema>,
  K extends StoreKey<PartViewerDBSchema, S>,
> = {
  storeName: S;
  keyPath: K;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  indexes?: ViewerDataStoreIndex<S, any, any>[];
};

type ViewerDataStoreIndex<
  S extends StoreNames<PartViewerDBSchema>,
  N extends IndexNames<PartViewerDBSchema, S>,
  INDEXKEY extends IndexKey<PartViewerDBSchema, S, N>,
> = {
  //https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore/createIndex
  name: N;
  keyPath: INDEXKEY;
  unique?: boolean;
  multiEntry?: boolean;
};

const partFileUuidString = 'partFileUuid' as const;
//const idsString = 'ids' as const;

export type ViewerDataResponseJson = {
  parts: AnyPartJson[];
  assemblies: AssemblyJson[];
  bodies: BodyJson[];
  //pcad provides assembly_instances
  assemblyInstances?: AssemblyInstanceJson[];
  //eslint-disable-next-line camelcase
  assembly_instances?: AssemblyInstanceJson[];
};

type HasPartFileUuid = { [partFileUuidString]: string };

type ViewerDataResponseJsonWithPartFileUuid = ViewerDataResponseJson &
  //partFileUuid is added to Json for indexing in indexedDB store
  HasPartFileUuid;

type PMIJsonWithPartFileUuid = PMIJson &
  //partFileUuid is added to Json for indexing in indexedDB store
  HasPartFileUuid;

export type FetchViewerData = (
  partFileUuid: string,
  options: PrepareAndMakeRequestOptions,
) => Promise<ViewerDataResponseJson>;

type PartViewerDBSchema = {
  [viewerDataStoreName]: {
    key: string; //key is the partFileUuid. It's type is string
    value: ViewerDataResponseJsonWithPartFileUuid;
    indexes: {
      //primary index is partFileUuid
      [partFileUuidString]: string;
    };
  };
  [partTessellationStoreName]: {
    key: string; //key is the partFileUuid. It's type is string
    value: (CloneableSTEPGeometry | CloneableSTLGeometry) & HasPartFileUuid;
    indexes: {
      //primary index is partFileUuid
      [partFileUuidString]: string;
    };
  };
  [viewerDataPMIStoreName]: {
    key: string; //key is the partFileUuid. It's type is string
    value: PMIJsonWithPartFileUuid;
    indexes: {
      //primary index is partFileUuid
      [partFileUuidString]: string;
    };
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const viewerDataStores: ViewerDataStore<any, any>[] = [
  { storeName: viewerDataStoreName, keyPath: partFileUuidString },
  { storeName: partTessellationStoreName, keyPath: partFileUuidString },
  { storeName: viewerDataPMIStoreName, keyPath: partFileUuidString },
];

type PartViewerDB = IDBPDatabase<PartViewerDBSchema>;

type GetOptions = {
  signal: AbortSignal;
  progress: CompositeProgressController;
  useCache?: boolean;
};

type GetPartOptions = GetOptions & {
  fetchViewerData: FetchViewerData;
};

const maxUIBlockMs = 50;

const compareVersions = (a?: string, b?: string) => {
  //* larger version first (1), smaller version last (-1), and = 0 if they are the same
  //* no version is last (-1) (unless both a and b are no version)
  if (typeof a !== 'string' && typeof b !== 'string') {
    return 0; //both have no version, so equal
  }
  if (typeof a !== 'string') {
    return -1; //a is less because it is not defined
  }
  if (typeof b !== 'string') {
    return 1; //a is greater because it is defined
  }
  const as = a.split('.');
  const bs = b.split('.');
  const maxLen = Math.max(as.length, bs.length);
  for (let i = 0; i < maxLen; i++) {
    //pad wth 0 if not defined
    const ai = parseInt(as[i], 10) || 0;
    const bi = parseInt(bs[i], 10) || 0;
    if (ai > bi) {
      return 1;
    } else if (ai < bi) {
      return -1;
    }
  }
  return 0;
};

//dbGet(): Helper to do async db.get() work and track progress
//dbPromise is defined if browser supports indexedDB
export const dbGet = async <
  T extends PartViewerDBSchema,
  S extends StoreNames<T>,
  K extends StoreKey<T, S>,
>(
  dbPromise: Promise<IDBPDatabase<T> | undefined>,
  storeName: S,
  key: K,
  options: SimpleProgressControllerOptions & {
    progress?: CompositeProgressController;
  } = {},
): Promise<StoreValue<T, S> | undefined> => {
  const db = await dbPromise;
  if (db) {
    const { progress, ...simpleProgressOptions } = options;
    const getProgress =
      progress?.isOpen && progress.pushSimpleProgress(simpleProgressOptions);
    try {
      const data = await db.get(storeName, key);
      if (getProgress) {
        getProgress.done(); //done checking indexedDB cache
      }
      return data;
    } catch (err) {
      if (getProgress) {
        getProgress.error(err);
      }
      throw err;
    }
  }
};

//dbPut(): Helper to do async db.put() work and track progress
//dbPromise is defined if browser supports indexedDB
export const dbPut = async <
  T extends PartViewerDBSchema,
  S extends StoreNames<T>,
>(
  dbPromise: Promise<IDBPDatabase<T> | undefined>,
  storeName: S,
  data: StoreValue<T, S>,
  options: SimpleProgressControllerOptions & {
    progress?: CompositeProgressController;
  } = {},
): Promise<void> => {
  const db = await dbPromise;
  if (db) {
    const { progress, ...simpleProgressOptions } = options;
    const putProgress =
      progress?.isOpen && progress.pushSimpleProgress(simpleProgressOptions);
    try {
      await db.put(storeName, data);
      if (putProgress) {
        putProgress.done(); //done checking indexedDB cache
      }
    } catch (err) {
      if (putProgress) {
        putProgress.error(err);
      }
      throw err;
    }
  }
};

//dbClear(): Helper to do async db.clear() work and track progress
//dbPromise is defined if browser supports indexedDB
export const dbClear = async <
  T extends PartViewerDBSchema,
  S extends StoreNames<T>,
>(
  dbPromise: Promise<IDBPDatabase<T> | undefined>,
  storeName: S,
  options: SimpleProgressControllerOptions & {
    progress?: CompositeProgressController;
  } = {},
): Promise<void> => {
  const db = await dbPromise;
  if (db) {
    const { progress, ...simpleProgressOptions } = options;
    const clearDBProgress =
      progress?.isOpen && progress.pushSimpleProgress(simpleProgressOptions);
    try {
      await db.clear(storeName);
      if (clearDBProgress) {
        clearDBProgress.done(); //done checking indexedDB cache
      }
    } catch (err) {
      if (clearDBProgress) {
        clearDBProgress.error(err);
      }
      throw err;
    }
  }
};

//About viewerDataService:
//* It is a singleton created with anonymous class expression so there is no temptation to recreate > 1 instance
//  (A class is used simply so to leverage private methods/properties. There are other ways to do this, but I like readability of class.)
//* It uses indexedDB stores as simple key/value stores that are accessible across tabs/windows/sessions
//* These stores are simply a cache for performance
//* Currently all of the stores are backed by data from endpoints and the stores are simply a cache for performance
//  * The data in these stores is considered immutable. We write once. And if we happen to blow away the db, its okay
//    because it can rebuild the cache with fetches.
//* In future we should wrap viewerDataService in serviceWorker.  This will allow the pendingCache to be shared
//  across tabs/windows/sessions

export const viewerDataService = new (class ViewerDataService {
  #pendingCache = new Map<
    string,
    [
      Promise<ViewerDataResponseJsonWithPartFileUuid | undefined>,
      CompositeProgressController,
    ]
  >(); //a temporary cache of Promises for fetched data until the data is received and added to indexedDB store

  #pendingCloneableGeometries = new Map<
    string,
    [
      WaitUntilSetController<
        CloneableSTEPGeometry | CloneableSTLGeometry | undefined
      >,
      SimpleProgressController,
    ]
  >();
  //#pendingFetchlatedData = new Map<string, Promise<CloneableSTEPGeometry | CloneableSTLGeometry | undefined>>();

  #pendingFetchTess = new Map<
    string,
    [Promise<TessJson | STLTessJson | undefined>, FetchProgressController]
  >();

  #pendingPMICache = new Map<
    string,
    [Promise<PMIJsonWithPartFileUuid | undefined>, CompositeProgressController]
  >(); //a temporary cache of Promises for fetched data until the data is received and added to indexedDB store
  #db: Promise<PartViewerDB | undefined>; //Promise to db (resolves to undefined if indexedDB is not supported)

  constructor() {
    //If indexedDB is defined, open the parviewerDB
    //* testing indexedDB first to avoid error being logged in sentry
    //  https://paperlessparts.atlassian.net/browse/PP-10399
    //* openDB is wrapped in new Promise() because it sometimes throws a
    //  ReferenceError in synchronous context that is not caught by `.catch()`
    //  By using a wrapped promise, we ensure the error thrown in is sync context
    //  results in rejected promise. (Seems strange but probably related to how openDB creates Promise)
    //* As extra insurance, it is wrapped in try/catch

    //private wrapper to this.#db (can't have private getter on class expression)
    const getDB = () => this.#db;

    //private wrapper to this.#db (can't have private method on class expression)
    const deleteAndCloseDB = async (_db?: PartViewerDB) => {
      try {
        if (_db) {
          _db.close();
        }
        //delete obsolete database (must be closed first)
        await deleteDB(partViewerDB);
      } catch (e) {
        //noop
      }
      this.#db = Promise.resolve(undefined);
    };

    let tries = 0;

    const instantiateDB = (): Promise<PartViewerDB | undefined> =>
      Object.prototype.hasOwnProperty.call(
        //globalThis is defined in modern browsers and we have fallbacks
        //eslint-disable-next-line no-undef
        globalThis || self || window,
        'indexedDB',
      )
        ? new Promise<PartViewerDB>((resolve) => {
            const openPromise = openDB<PartViewerDBSchema>(
              partViewerDB,
              partViewerDBVersion,
              {
                upgrade: (upgradeDb /*, oldVersion*/) => {
                  //Note: if there is no partViewerDB, oldVersion === 0

                  //delete existing stores (storeNames that we still want will be re-created and re-populated)
                  for (const storeName of upgradeDb.objectStoreNames) {
                    upgradeDb.deleteObjectStore(storeName);
                  }
                  //create storeNames that do not exist
                  for (const {
                    storeName,
                    keyPath,
                    indexes,
                  } of viewerDataStores) {
                    if (!upgradeDb.objectStoreNames.contains(storeName)) {
                      const store = upgradeDb.createObjectStore(storeName, {
                        keyPath,
                      });
                      if (indexes) {
                        for (const {
                          name,
                          keyPath,
                          multiEntry,
                          unique,
                        } of indexes) {
                          store.createIndex(name, keyPath, {
                            multiEntry,
                            unique,
                          });
                        }
                      }
                    }
                  }
                },
              },
            );
            resolve(openPromise);
          })
            .then((db: PartViewerDB): PartViewerDB => {
              //When db is created, add 'versionchange' listener'
              //* Watch for the partViewer DB (in indexedDB) to change and close the reference to indexedDB. This happens when we do a release and user has
              //  tabs/windows open with prior release.  By closing the DB, the new window/tab with the new version of the DB can proceed with upgrade.
              const versionChangeListener = async (
                e: IDBVersionChangeEvent,
              ) => {
                const _db = await getDB(); //make sure it still exists
                if (_db) {
                  const newVersion = e?.newVersion ?? 'unknown'; //'unknown' for safety
                  const oldVersion = e?.oldVersion ?? partViewerDBVersion;
                  if (partViewerDBVersion !== newVersion) {
                    console.warn(
                      `${partViewerDB} indexedDB database was upgraded from ${newVersion} to ${oldVersion} in a different window/tab. ${partViewerDB} will be closed. This tab/window must be reloaded.`,
                    );
                    await deleteAndCloseDB(_db);
                  }
                }
              };
              const closeListener = () => {
                db.removeEventListener(
                  dbVersionChangeEventName,
                  versionChangeListener,
                );
                db.removeEventListener(dbCloseEventName, closeListener);
              };
              db.addEventListener(
                dbVersionChangeEventName,
                versionChangeListener,
              );
              db.addEventListener(dbCloseEventName, closeListener);

              return db;
            })
            .catch(async (err) => {
              //If there is an error, it is suppressed by undefinedPromise.
              //* It does not prevent functionality and outputting an error message would clutter output of Jest unit tests.
              const message =
                typeof (err as Error)?.message === 'string'
                  ? (err as Error)?.message
                  : '';
              if (
                tries < 1 &&
                message.includes('is less than the existing version')
              ) {
                // When upgrading to a version of indexedDB partViewer DB that is lower
                // than current version, delete the current DB. On the second try of instantiateDB(), the partViewer DB will be created without issue.
                // This saves us having to go through special work to revert a release that included an indexedDB version change to the partViewer DB.
                try {
                  const existingVersionMatches = message.match(
                    /existing version \((\d+)\)/,
                  );
                  const existingVersion = existingVersionMatches
                    ? parseInt(existingVersionMatches[1], 10)
                    : undefined;
                  if (existingVersion) {
                    console.error(
                      `${partViewerDB} indexedDB database version is ${existingVersion}, but ${partViewerDBVersion} is desired. Attempting to delete it so it can be re-created.`,
                    );
                  }
                  await deleteAndCloseDB(undefined);
                  tries++;
                  //Try again (at most) once more after deleting db
                  return instantiateDB();
                } catch (e) {
                  //noop: will return undefined (not supported)
                }
              }
              return undefined; // Undefined means it is not supported.
            })
        : Promise.resolve(undefined); // Undefined means it is not supported.

    this.#db = instantiateDB();
  }

  clearPMICache() {
    this.#pendingPMICache.clear();
  }

  clearFetchTess() {
    this.#pendingFetchTess.clear();
  }

  clearCloneableGeometries() {
    this.#pendingCloneableGeometries.clear();
  }

  clearPendingCache() {
    this.#pendingCache.clear();
  }

  getPartDataId(partFileUuid: string) {
    return `${partFileUuid}-getData`;
  }
  getBodyOrAssemblyInstanceLoadingId(bodyId: string) {
    //Note: Body.suffix is used for both body and assemblyInstance progresses!
    return `${bodyId}${Body.suffix}`;
  }

  getPartFileLoadingId(partId: string) {
    return `${partId}-partFile`;
  }

  getPartLoadingId(partId: string) {
    return `${partId}${Part.suffix}`;
  }

  getInspectId(partFileUuid: string) {
    return `${partFileUuid}-inspect`;
  }
  getEstablishAssemblyStructureId(partFileUuid: string) {
    return `${partFileUuid}-establishAsmStruct`;
  }
  getTesslatedDataId(partFileUuid: string) {
    return `${partFileUuid}-tess-getCache`;
  }
  getFetchTessId(id: string) {
    return `${id}-fetchTess`;
  }
  putTesslatedDataId(partFileUuid: string) {
    return `${partFileUuid}-tess-putCache`;
  }
  getGeometriesFromCacheId(partFileUuid: string) {
    return `${partFileUuid}-getGeometriesFromCache`;
  }
  getFetchDataFromCacheId(partFileUuid: string) {
    return `${partFileUuid}-getCache`;
  }
  getFetchDataId(partFileUuid: string) {
    return `${partFileUuid}-fetch`;
  }
  putFetchDataFromCacheId(partFileUuid: string) {
    return `${partFileUuid}-putCache`;
  }
  getPMIDataId(partFileUuid: string) {
    return `${partFileUuid}-getPMIData`;
  }
  getPMIDataInnerId(partFileUuid: string) {
    return `${partFileUuid}-getPMIDataInner`;
  }
  getPMIDataFromCacheId(partFileUuid: string) {
    return `${partFileUuid}-pmi-getCache`;
  }
  getPMIDataFetchId(partFileUuid: string) {
    return `${partFileUuid}-pmi-fetch`;
  }
  putPMICacheId(partFileUuid: string) {
    return `${partFileUuid}-pmi-putCache`;
  }

  async establishAssemblyStructure(
    bodies: AnyBody[], //new bodies
    assemblyInstances: AssemblyInstance[], //new assemblies
    bodyOrAssemblyInstanceById: Map<string, AnyBody | AssemblyInstance>, //may contain more than bodies & assemblyInstances
    establishAssemblyStructureProgress: CompositeProgressController | undefined,
    lastPause: number,
  ): Promise<Set<Group>> {
    const rootGroups = new Set<Group>();
    const ids: string[] = [];
    // construct parent child group management
    // establish which group is actually the one we add to the scene environment
    for (const bodyOrAssemblyInstance of chain<AnyBody | AssemblyInstance>(
      bodies,
      assemblyInstances,
    )) {
      const { id, isMaterial, isRoot } = bodyOrAssemblyInstance;
      if (typeof id === 'string') {
        ids.push(id);
      }
      if (isMaterial) continue; //can't be a childBody or childAssembly, or root
      if (isRoot) {
        rootGroups.add(bodyOrAssemblyInstance.group);
      }
      if (bodyOrAssemblyInstance instanceof AssemblyInstance) {
        //Set assemblyInstance for clarity: we know bodyOrAssemblyInstance is an assemblyInstance
        const assemblyInstance = bodyOrAssemblyInstance;
        for (const id of assemblyInstance.objectIds) {
          const childBodyOrAssemblyInstance:
            | AssemblyInstance
            | AnyBody
            | undefined = bodyOrAssemblyInstanceById.get(id);
          assemblyInstance.addChild(childBodyOrAssemblyInstance);
        }
        //eslint-disable-next-line no-param-reassign
        lastPause = await assemblyInstance.finishedAddingChildren(
          lastPause,
          establishAssemblyStructureProgress,
        );
      }
      //Don't block UI if this is taking > maxUIBlockMs
      const now = performance.now();
      if (now - lastPause > maxUIBlockMs) {
        await microWait();
        //eslint-disable-next-line no-param-reassign
        lastPause = now;
      }
    }

    if (establishAssemblyStructureProgress) {
      establishAssemblyStructureProgress.close();
    }

    return rootGroups;
  }

  //getPartFileUuid()
  //* Given top level partFileUuid, gets its parts, assemblies, bodies, assemblyInstances and rootGroups
  //* partOrAssemblyById, bodyOrAssemblyInstanceById, and bodyIdsByPartFileUuid
  //  are lookups that are both used by and populated by getPartFileUuid()
  //* returns the new parts, assemblies, bodies, assemblyInstances and rootGroups
  async getPartFileUuid(
    partFileUuid: string,
    shouldPremptivelyLoadMore: boolean | undefined,
    partOrAssemblyById: Map<string, AnyPart | Assembly>,
    bodyOrAssemblyInstanceById: Map<string, AnyBody | AssemblyInstance>,
    bodyIdsByPartFileUuid: MultiMap<string, string>,
    renderer: Renderer,
    options: GetPartOptions & { lastPause?: number },
  ): Promise<{
    bodies: AnyBody[];
    lastPause: number;
    assemblyInstances: AssemblyInstance[];
    rootGroups: Set<Group>;
  }> {
    const parts: AnyPart[] = [];
    const assemblies: Assembly[] = [];
    const bodies: AnyBody[] = [];
    const assemblyInstances: AssemblyInstance[] = [];
    const { signal, progress, fetchViewerData, useCache = true } = options;
    let lastPause = options.lastPause || performance.now();

    const inspectPartDataProgress = progress.pushSimpleProgress({
      id: this.getInspectId(partFileUuid),
      todoCount: 1,
      expectedMs: 1000,
      description: 'Loading Part',
      start: false,
    });
    const data = await this.#getPartFileUuidData(partFileUuid, {
      signal,
      fetchViewerData,
      progress,
      useCache,
    });
    if (!data) {
      inspectPartDataProgress.done(); //Ok to call done() if start() not called. startedAt will equal finishedAt
      return {
        lastPause,
        bodies,
        assemblyInstances,
        rootGroups: new Set(),
      };
    }

    //pcad provides assembly_instances, whereas paperlessparts app provides assemblyInstances
    const dataAssemblyInstances =
      data.assemblyInstances ?? data.assembly_instances ?? [];
    const assemblyInstanceIds = new Set<string>(
      dataAssemblyInstances.map(
        (assemblyInstanceData) => assemblyInstanceData.id as string,
      ),
    );
    const derivedFromParts = new Map<string, string>(); //map of uuid to flatPartFileUuid

    inspectPartDataProgress.start(
      data.parts.length +
        data.bodies.length +
        data.assemblies.length +
        dataAssemblyInstances.length,
    );

    let progressCount = 0;

    const partGeometriesToGet: PartGeometryToGet[] = [];

    //Load the parts (but not their geometry)
    for (const partData of data.parts) {
      const uuid = partData.partFileUuid || (partData.uuid as string);
      //Only instantiate/add new parts (There is edge case where some partFileUuids are in BOM twice)
      if (!partOrAssemblyById.has(uuid)) {
        const part = new Part(partData, renderer, progress, signal);
        const { partDataToLoad } = part;
        if (partDataToLoad) {
          partGeometriesToGet.push(partDataToLoad);
        }
        partOrAssemblyById.set(part.id as string, part);
        parts.push(part);
      }

      progressCount++;
      //Don't block UI if this is taking > maxUIBlockMs
      const now = performance.now();
      if (now - lastPause > maxUIBlockMs) {
        inspectPartDataProgress.addProgress(progressCount);
        progressCount = 0;
        await microWait();
        lastPause = now;
      }
    }

    //Rebuild derivedFromParts (based on all parts (not just ones created from data.parts))
    for (const part of partOrAssemblyById.values()) {
      if (part instanceof Part) {
        if (part.derivedFromFileUuid) {
          //cached in lookup so it can be set when we know we have all parts constructed
          derivedFromParts.set(part.derivedFromFileUuid, part.id as string);
        }
      }
    }

    //If a flat part is created, the original formed part's instance may not have
    //the flatPartFileUuid assigned because it was constructed or cached BEFORE the
    //the flat part existed.  When the flat part is defined, we can assign the flat part
    //* See https://paperlessparts.atlassian.net/browse/PP-10607
    for (const [uuid, flatPartFileUuid] of derivedFromParts.entries()) {
      const part = partOrAssemblyById.get(uuid) as AnyPart;
      if (!part) {
        continue; //skip because the part isn't loaded yet. It will be updated in future call to getPartFileUuid()
      }
      if (part.flatPartFileUuid && part.flatPartFileUuid !== flatPartFileUuid) {
        //Unlikely to happen. But logging in case there is > 1 derived flat parts from same part file in future.
        console.warn(
          `Warning, part ${part.id}'s flatPartFileUuid changed from ${part.flatPartFileUuid} to ${flatPartFileUuid}`,
        );
      }
      part.flatPartFileUuid = flatPartFileUuid;
    }

    //Load the bodies (but not their geometry)
    for (const bodyData of data.bodies) {
      const bodyId = bodyData.id as string;
      if (!bodyOrAssemblyInstanceById.has(bodyId)) {
        const part = partOrAssemblyById.get(
          bodyData.partFileUuid || (bodyData.part_uuid as string),
        ) as AnyPart;
        const body = new Body(bodyData, part, renderer, progress, signal);
        if (part.hasGeometry) {
          bodyOrAssemblyInstanceById.set(bodyId, body);
          bodyIdsByPartFileUuid.set(body.partUuid as string, body.id as string);
        }
        bodies.push(body);
      }
      progressCount++;
      //Don't block UI if this is taking > maxUIBlockMs
      const now = performance.now();
      if (now - lastPause > maxUIBlockMs) {
        inspectPartDataProgress.addProgress(progressCount);
        progressCount = 0;
        await microWait();
        lastPause = now;
      }
    }

    //Load assemblies
    for (const assemblyData of data.assemblies) {
      const uuid = assemblyData.partFileUuid || (assemblyData.uuid as string);
      if (!partOrAssemblyById.has(uuid)) {
        const assembly = new Assembly(assemblyData, renderer);
        partOrAssemblyById.set(uuid, assembly);
        assemblies.push(assembly);
      }
      progressCount++;
      //Don't block UI if this is taking > maxUIBlockMs
      const now = performance.now();
      if (now - lastPause > maxUIBlockMs) {
        inspectPartDataProgress.addProgress(progressCount);
        progressCount = 0;
        await microWait();
        lastPause = now;
      }
    }

    //Load assemblyInstances
    for (const assemblyInstanceData of dataAssemblyInstances) {
      const assemblyInstanceId = assemblyInstanceData.id as string;
      if (!bodyOrAssemblyInstanceById.has(assemblyInstanceId)) {
        //objectIds are filtered to ensure objectIds are known
        //Note: pcad provides assemblyInstanceData.object_ids
        assemblyInstanceData.objectIds = (
          assemblyInstanceData.childViewerIds ||
          assemblyInstanceData.object_ids ||
          []
        ).filter(
          (childViewerId) =>
            bodyOrAssemblyInstanceById.has(childViewerId) ||
            assemblyInstanceIds.has(childViewerId),
        );

        const assemblyInstance = new AssemblyInstance(
          assemblyInstanceData,
          partOrAssemblyById.get(
            (assemblyInstanceData.partFileUuid ||
              assemblyInstanceData.part_uuid) as string,
          ) as Assembly,
          renderer,
          progress,
          signal,
        );
        bodyOrAssemblyInstanceById.set(assemblyInstanceId, assemblyInstance);
        bodyIdsByPartFileUuid.set(
          assemblyInstance.partUuid as string,
          assemblyInstance.id as string,
        );
        assemblyInstances.push(assemblyInstance);
      }
      progressCount++;
      //Don't block UI if this is taking > maxUIBlockMs
      const now = performance.now();
      if (now - lastPause > maxUIBlockMs) {
        inspectPartDataProgress.addProgress(progressCount);
        progressCount = 0;
        await microWait();
        lastPause = now;
      }
    }

    //Remove the placeholder inspectPartDataProgress now that other progress items added to progress
    inspectPartDataProgress.done();

    const rootGroups = await this.establishAssemblyStructure(
      bodies,
      assemblyInstances,
      bodyOrAssemblyInstanceById,
      shouldPremptivelyLoadMore
        ? progress.pushCompositeProgress({
            id: this.getEstablishAssemblyStructureId(partFileUuid),
            expectedMs: 50,
            description: dataAssemblyInstances.length
              ? 'Creating Assembly Structure'
              : 'Finalizing',
          })
        : undefined,
      lastPause,
    );

    //Kick off requests for geometries, but don't wait
    //For secondary part files, it's not a good idea to pre-emptively get their geometries
    //because if the user never views those secondary part files, then it is a waste and the
    //promise for each of their geometries is left pending for lifecycle of part viewer
    if (shouldPremptivelyLoadMore) {
      const partName = partOrAssemblyById.get(partFileUuid)?.name;
      this.#getGeometries(partGeometriesToGet, {
        id: this.getGeometriesFromCacheId(partFileUuid),
        description: `Retrieving Part Geometry${
          partName ? `: ${partName}` : ''
        }`,
        undefined,
        signal,
      });
    }

    //Note: parts, assemblies, bodies, assemblyInstances are constructed, but their geometry is not loaded
    //asyncLoad() must be called on the visible body/assemblyInstance ids

    return {
      lastPause,
      bodies,
      assemblyInstances,
      rootGroups,
    };
  }

  async getPMIData<T extends AnyPart | Assembly>(
    partOrAssembly: T,
    authenticatedUrl: string,
    isVisible: boolean | undefined,
    {
      signal,
      progress,
      useCache = true,
    }: {
      signal: AbortSignal;
      progress: CompositeProgressController;
      useCache?: boolean;
    },
  ): Promise<T> {
    if (
      partOrAssembly &&
      !partOrAssembly.pmi &&
      partOrAssembly.id &&
      authenticatedUrl
    ) {
      const partFileUuid = partOrAssembly.id;
      const pmiJson = await this.#getPMIDataJson(
        partFileUuid,
        authenticatedUrl,
        {
          signal,
          progress,
          useCache,
        },
      );
      if (pmiJson) {
        const pmi = new PMIData(pmiJson);
        partOrAssembly.pmi = pmi;
        await pmi.hydrate(isVisible);
      }
    }

    return partOrAssembly;
  }

  //Kick of single transaction of requests for geometry from indexedDB cache
  //* This is intended to kick start a bulk transaction, which in aggregate is faster than separate dbGet calls
  //* Should not be called with partFileUuids of parts that already have their geometry loaded
  //* Each geometry's async result is added to this.#pendingCloneableGeometries
  //* Does not hydrate the cloneableTessData. (This is left for priority queue to deal with)
  //* Does not call this.removePendingGeometry(partFileUuid)
  //  * the loader for `Part` will cleanup once the cloneableTessData is hydrated (which could require fetching and transformation to cloneableTessData)
  //* When geometry is not in cache, pre-emptively begins fetching the tessJson (so it will be ready downstream)
  async #getGeometries(
    partsToGet: PartGeometryToGet[],
    options: SimpleProgressControllerOptions & {
      signal?: AbortSignal;
    } = {},
  ): Promise<void> {
    const { progress, signal, ...simpleProgressOptions } = options;
    if (signal && signal.aborted) {
      return;
    }
    const db = await this.#db;
    if (db) {
      const progressOptions = {
        ...simpleProgressOptions,
        expectedMs: 1000,
        extendAmountMs: 1000,
      };
      const getProgress = progress?.isOpen
        ? progress.pushSimpleProgress(progressOptions)
        : new SimpleProgressController(progressOptions);
      try {
        const tx = db.transaction(partTessellationStoreName, 'readonly');
        const store = tx.store;
        const gets: Promise<
          [
            PartGeometryToGet | undefined,
            CloneableSTEPGeometry | CloneableSTLGeometry | undefined,
          ]
        >[] = [];
        for (const partDataToGet of partsToGet) {
          const { partFileUuid } = partDataToGet;
          if (!this.#pendingCloneableGeometries.has(partFileUuid)) {
            const pending = new WaitUntilSetController<
              CloneableSTEPGeometry | CloneableSTLGeometry | undefined
            >();
            this.#pendingCloneableGeometries.set(partFileUuid, [
              pending,
              getProgress,
            ]);
            gets.push(Promise.all([partDataToGet, store.get(partFileUuid)]));
          }
        }
        gets.push(
          Promise.all([undefined, tx.done]) as Promise<[undefined, undefined]>,
        );
        const fetches: Promise<unknown>[] = [];
        for (const [partDataToGet, cloneableTessData] of await Promise.all(
          gets,
        )) {
          if (partDataToGet) {
            const { partFileUuid } = partDataToGet;
            const [pendingCloneableGeometry] =
              this.#pendingCloneableGeometries.get(partFileUuid as string) ??
              [];
            if (pendingCloneableGeometry) {
              if (cloneableTessData) {
                pendingCloneableGeometry.set(cloneableTessData);
              } else {
                //resolve promise. (no cloneableTessData in cache)
                pendingCloneableGeometry.set(undefined);
                if (!(signal && signal.aborted)) {
                  const { authenticatedUrl, partName } = partDataToGet;
                  //Kick off fetch, but don't wait here
                  fetches.push(
                    this.fetchTesslatedData(
                      partFileUuid,
                      authenticatedUrl,
                      partName,
                      //no parent progress tracking for this pre-emptive fetch
                      undefined,
                      signal,
                    )[0],
                  );
                }
              }
            }
          }
        }
        if (getProgress) {
          //Done getting cloneable geometries that were in cache
          getProgress.done();
        }
        await Promise.all(fetches);
      } catch (err) {
        if (getProgress) {
          getProgress.error(err);
        }
        throw err;
      } finally {
        if (getProgress) {
          progress?.remove(getProgress);
          getProgress.signal.disposeIfUnused();
        }
      }
    } else {
      //No indexedDB, so kick of fetches of tess Json for all geometry
      await Promise.all(
        partsToGet.map(({ partFileUuid, authenticatedUrl, partName }) => {
          this.fetchTesslatedData(
            partFileUuid,
            authenticatedUrl,
            partName,
            progress,
            signal,
          )[0];
        }),
      );
    }
  }

  removePendingGeometry(partFileUuid: string) {
    const [pendingCloneableGeometry, pendingProgress] =
      this.#pendingCloneableGeometries.get(partFileUuid) ?? [];
    if (pendingCloneableGeometry) {
      //setReject is noop if already resolved
      pendingCloneableGeometry.setReject(
        'Stopped waiting for pending geometry',
      );
      this.#pendingCloneableGeometries.delete(partFileUuid);
    }
    if (pendingProgress) {
      pendingProgress.done();
    }
  }

  //getGeometry(): Gets the cloneable geometry
  //* First checks the pending cache (which it should be in because of pre-emptive getGeometries() call that makes all requests in single transaction)
  //* fallback is manual dbGet
  async getGeometry(
    partFileUuid: string,
    partName?: string,
    progress?: CompositeProgressController,
  ): Promise<CloneableSTEPGeometry | CloneableSTLGeometry | undefined> {
    const options = {
      id: this.getTesslatedDataId(partFileUuid),
      expectedMs: 1000,
      extendAmountMs: 1000,
      description: partName
        ? `Fetching part tessellation data for ${partName}`
        : 'Fetching part tessellation data',
    };
    if (this.#pendingCloneableGeometries.has(partFileUuid)) {
      const [pendingCloneableGeometry, pendingProgress] =
        this.#pendingCloneableGeometries.get(partFileUuid) ?? [];
      if (progress && pendingProgress) {
        progress.push(pendingProgress); //current `progress` needs to track the pendingProgress
      }
      return pendingCloneableGeometry?.waitUntilSet;
    } else {
      //unlikely case because the pre-emptive this.#getGeometries() should have been called already
      return dbGet(this.#db, partTessellationStoreName, partFileUuid, {
        progress,
        ...options,
      });
    }
  }

  cacheTesslatedData(
    partFileUuid: string,
    data: CloneableSTEPGeometry | CloneableSTLGeometry,
    partName: string,
    progress?: CompositeProgressController,
  ): Promise<void> {
    return dbPut(
      this.#db,
      partTessellationStoreName,
      { ...data, partFileUuid },
      {
        id: this.putTesslatedDataId(partFileUuid),
        progress,
        expectedMs: 1000,
        extendAmountMs: 1000,
        description: partName
          ? `Caching part tessellation data for ${partName}`
          : 'Cachine part tessellation data',
      },
    );
  }

  fetchTesslatedData(
    id: string,
    authenticatedUrl: string,
    partName: string,
    progress?: CompositeProgressController,
    signal?: AbortSignal,
  ): [Promise<TessJson | STLTessJson | undefined>, FetchProgressController] {
    let pendingFetch = this.#pendingFetchTess.get(id);
    if (pendingFetch) {
      if (progress) {
        const [, pendingProgress] = pendingFetch;
        if (pendingProgress) {
          progress.push(pendingProgress); //current `progress` needs to track the pendingProgress
        }
      }
    } else {
      pendingFetch = this.#fetchTesslatedData(
        id,
        authenticatedUrl,
        partName,
        progress,
        signal,
      );
      this.#pendingFetchTess.set(id, pendingFetch);
    }
    return pendingFetch;
  }

  #fetchTesslatedData(
    id: string,
    authenticatedUrl: string,
    partName: string,
    progress?: CompositeProgressController,
    signal?: AbortSignal,
  ): [Promise<TessJson | STLTessJson | undefined>, FetchProgressController] {
    //If there is a fetch error, this will be error presented to progress controller
    const messageForFetchError = partName
      ? `Error fetching tessellation data for ${partName}`
      : 'Error fetching tessellation data';
    const tessDescription = partName
      ? `Fetching part tessellation data for ${partName}`
      : 'Fetching Part tessellation data';
    const progressOptions = {
      id: this.getFetchTessId(id),
      messageForFetchError,
      estimatedCompressFactor: 4, //estimate for gzip
      description: tessDescription,
      requestDescription: tessDescription,
    };
    const fetchProgress =
      progress?.pushFetchProgress(progressOptions) ??
      new FetchProgressController(progressOptions);

    return [
      (async () => {
        const response = await fetchAndTrack(authenticatedUrl, {
          ...(signal && { signal }),
          ...(fetchProgress && { fetchProgress }),
        });
        if (response.ok) {
          const tess = await response.json();
          return tess
            ? ((tess.tess ?? tess.general ?? tess) as TessJson | STLTessJson)
            : undefined;
        } else {
          throw new Error(
            `${messageForFetchError}, status: ${response.status}`,
          );
        }
      })(),
      fetchProgress,
    ];
  }

  public removePendingFetch(id: string) {
    this.#pendingFetchTess.delete(id);
  }

  async #getPartFileUuidData(
    partFileUuid: string,
    {
      signal,
      progress: parentProgress,
      fetchViewerData,
    }: //Note: The cache for viewerData is disabled right now because with the priority queue
    //there is an edge case where an authenticatedUrl may become expired before the data is fetched
    //We need mechanism for the frontend to create the authenticatedUrl with a backend service so that it is always current
    //Typically this would be done by providing an authenticateService to the requestService. The authenticateService could
    //rely on backend service to get authorization token.
    //* Note: other cached stores are still used. For example, once the tess if fetched from the authenticatedUrl, it will be cached
    //  in the `partTessellationStoreName` store. And later if there data is found in the tess store, then the authenticatedUrl is not
    //  used.  Fortunately the fetch request for viewerData is quick and not much slower than indexedDB.
    //* Note: We could still use the cache and check every authenticatedUrl to see if it is expired, but most likely it will be expired, yet
    //  still be unneeded because data will be in `partTessellationStoreName`. The test to see if each is in `partTessellationStoreName` using
    //  a single transaction is possible, but not worth effort.
    //useCache = true,
    GetPartOptions,
  ): Promise<ViewerDataResponseJsonWithPartFileUuid | undefined> {
    //We don't know how parentProgress will be used, so create progress for this.#getPartFileUuidData
    const progress = parentProgress.pushCompositeProgress({
      id: this.getPartDataId(partFileUuid),
      description: 'Loading Part Data',
    });

    const [pendingData, pendingProgress] =
      this.#pendingCache.get(partFileUuid) || [];
    if (pendingData) {
      progress.push({ signal: pendingProgress?.signal });
      progress.close();
      return pendingData;
    }

    const useCache = false;

    let data: ViewerDataResponseJsonWithPartFileUuid | undefined;

    if (useCache) {
      // //For Development: Clear database is for development only
      // for (const {storeName} of viewerDataStores) {
      //   if (storeName !== viewerDataPMIStoreName) {
      //     await dbClear(this.#db, storeName, {
      //       expectedMs: 100,
      //       extendAmountMs: 50,
      //       description: 'Clearing Cache for development',
      //       progress,
      //     });
      //   }
      // }
      // await microWait(); //wait so react can react to change in progress
      data = await dbGet(this.#db, viewerDataStoreName, partFileUuid, {
        id: this.getFetchDataFromCacheId(partFileUuid),
        progress,
        //When checking the cache it can take anywhere from 1 - 10+ seconds depending on
        //size of model. This is just an estimate.
        //Subjectively it looks best if we pick a lower bound.
        //Idea: Could store previous load time to aide with choosing a better value.
        expectedMs: 1000,
        extendAmountMs: 1000,
        description: 'Fetching Part Data', //From cache
      });
      await microWait(); //wait so react can react to change in progress
      if (signal.aborted) {
        throw new AbortError('Aborted: Parts Viewer was closed');
      }
    }
    if (!data) {
      //data is not in indexedDB. So fetch it and add it to indexedDB
      //As well, dataPromise is cached in case getPartFileUuid() is
      //called before fetchViewerData and dbPut() finish
      const dataPromise: Promise<
        ViewerDataResponseJsonWithPartFileUuid | undefined
      > = (async () => {
        const dataRequestDescription = 'Fetching part data';
        const fetchProgress = progress.pushFetchProgress({
          id: this.getFetchDataId(partFileUuid),
          description: dataRequestDescription,
          requestDescription: dataRequestDescription,
        });
        const _data: ViewerDataResponseJson & Partial<HasPartFileUuid> =
          await fetchViewerData(partFileUuid, { signal, fetchProgress });
        await microWait();
        if (_data) {
          if (useCache) {
            //Only cache results IF all tessellated data was fetched successfully. (and useCache is true)
            //* This means that if there was a failure, then when the partFileUuid is loaded again, we'll re-fetch and
            //  not use the cache; which gives PaperlessParts an opportunity to fix the missing data or correct the
            //  authenticatedUrl in future.
            await dbPut(
              this.#db,
              viewerDataStoreName,
              //TODO: Add userGroup to `_data` so we can test if cached entry is usable for current userGroup
              Object.assign(_data, { partFileUuid }),
              {
                id: this.putFetchDataFromCacheId(partFileUuid),
                progress,
                expectedMs: 100,
                extendAmountMs: 50,
                description: 'Caching part data',
              },
            );
          }

          if (signal.aborted) {
            throw new AbortError(
              `Abort signal was received: Stopped getting ${partFileUuid}`,
            );
          }
        }
        return _data as ViewerDataResponseJsonWithPartFileUuid | undefined;
      })();
      this.#pendingCache.set(partFileUuid, [dataPromise, progress]);
      try {
        data = await dataPromise;
      } catch (e) {
        if (isAbortErrorLike(e)) {
          //user navigated away from page. Can't reuse an aborted promise
          this.#pendingCache.delete(partFileUuid);
        }
        throw e;
      }
      this.#pendingCache.delete(partFileUuid); //Note: cached value persists if dataPromise throws
    }
    progress.close();
    return data;
  }

  async #getPMIDataJson(
    partFileUuid: string,
    authenticatedUrl: string,
    { signal, progress: parentProgress, useCache = true }: GetOptions,
  ): Promise<PMIJsonWithPartFileUuid | undefined> {
    //We don't know how parentProgress will be used, so create progress for getPMIData
    const progress = parentProgress.pushCompositeProgress({
      id: this.getPMIDataInnerId(partFileUuid),
    });

    const [pendingData, pendingProgress] =
      this.#pendingPMICache.get(partFileUuid) || [];
    if (pendingData) {
      if (pendingProgress) {
        progress.push({ signal: pendingProgress?.signal });
      }
      progress.close();
      return pendingData;
    }

    let data: PMIJsonWithPartFileUuid | undefined;
    const description = 'Fetching PMI data.';

    if (useCache) {
      //For Development: Clear database is for development only
      // await dbClear(this.#db, viewerDataPMIStoreName, progress, {
      //   expectedMs: 100,
      //   extendAmountMs: 50,
      //   description: 'Clearing Cache for development',
      // });
      // await microWait(); //wait so react can react to change in progress
      data = await dbGet(this.#db, viewerDataPMIStoreName, partFileUuid, {
        id: this.getPMIDataFromCacheId(partFileUuid),
        progress,
        //When checking the cache it can take anywhere from 1 - 10+ seconds depending on
        //size of model. This is just an estimate.
        //Subjectively it looks best if we pick a lower bound.
        //Idea: Could store previous load time to aide with choosing a better value.
        expectedMs: 1000,
        extendAmountMs: 1000,
        description,
      });
      await microWait(); //wait so react can react to change in progress
      if (signal.aborted) {
        throw new AbortError(
          `Abort signal was received: Stopped getting ${partFileUuid}`,
        );
      }
    }
    if (!data) {
      //data is not in indexedDB. So fetch it and add it to indexedDB
      //As well, dataPromise is cached in case getPMIData is called before fetch and dbPut() finish
      const dataPromise: Promise<PMIJsonWithPartFileUuid | undefined> =
        (async () => {
          const messageForFetchError = `Error fetching PMI data for ${partFileUuid}`;
          const response = await fetchAndTrack(authenticatedUrl, {
            signal,
            fetchProgress: progress.pushFetchProgress({
              id: this.getPMIDataFetchId(partFileUuid),
              messageForFetchError,
              estimatedCompressFactor: 4, //estimate for gzip
              description: description,
              requestDescription: description,
            }),
          });
          if (response.ok) {
            try {
              data = await response.json();
              if (!data || compareVersions(data.version, minPMIVersion) < 0) {
                return; //no JSON
              }
            } catch (e) {
              console.error(`Unexpected PMI Json for ${partFileUuid}`);
              return; //no JSON
            }
            if (useCache && data.partFileUuid !== partFileUuid) {
              //primary key must be on object for indexedDB if not autoIncrement
              data.partFileUuid = partFileUuid;
            }
            await dbPut(this.#db, viewerDataPMIStoreName, data, {
              id: this.putPMICacheId(partFileUuid),
              progress,
              expectedMs: 100,
              extendAmountMs: 50,
              description: 'Caching pmi data',
            });
            return data;
          } else {
            console.error(
              `${messageForFetchError}, status: ${response.status}`,
            );
            return undefined;
          }
        })();
      this.#pendingPMICache.set(partFileUuid, [dataPromise, progress]);
      try {
        data = await dataPromise;
      } catch (e) {
        if (isAbortErrorLike(e)) {
          //user navigated away from page. Can't reuse an aborted promise
          this.#pendingPMICache.delete(partFileUuid);
        }
        throw e;
      }
      this.#pendingPMICache.delete(partFileUuid); //Note: cached value persists if dataPromise throws
    }
    progress.close();
    return data;
  }
})();
